import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, TooltipProvider, cn } from "@nexus/ui";
import type { CriterionFinding } from "@nexus/contracts";

import type { PreflightReportDetail } from "../../types/preflight.types";
import { TONE_BG } from "../../constants/result-state";
import { scoredAssetCount } from "../../lib/report-blockers";
import { computeReportFindings } from "../../lib/report-findings";
import { CollapsibleSection } from "../CollapsibleSection/CollapsibleSection";
import { CriterionLabel } from "../CriterionLabel/CriterionLabel";

/** Matches the composition list: the panel height stays put however many findings there are. */
const LIST_MAX_HEIGHT_CLASS = "max-h-60";

function FindingRow({
  finding,
  rank,
  scoredCount,
  maxAssetCount,
  mounted,
}: {
  finding: CriterionFinding;
  rank: number;
  scoredCount: number;
  maxAssetCount: number;
  mounted: boolean;
}) {
  const { t } = useTranslation();
  // Bar width is relative to the most widespread finding, the way the blockers and composition
  // lists scale theirs — the leading row fills the track and the rest read against it.
  const width =
    maxAssetCount > 0 ? (finding.assetCount / maxAssetCount) * 100 : 0;
  const failWidth = width * (finding.failedCount / finding.assetCount);
  const partialWidth = width * (finding.partialCount / finding.assetCount);

  return (
    <li className="flex flex-col gap-1.5 px-2 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden
            className="text-muted-foreground w-3 shrink-0 text-[11px] font-semibold tabular-nums"
          >
            {rank}
          </span>
          <span className="text-foreground min-w-0 truncate text-sm font-medium">
            {finding.subCheck}
          </span>
          {/* What the analyzer measured, so the rule reads as a finding and not just a label. */}
          {finding.examples.length > 0 ? (
            <span className="text-muted-foreground min-w-0 truncate text-xs">
              {finding.otherExampleCount > 0
                ? t("preflight.findings.examplesMore", {
                    examples: finding.examples.join(", "),
                    extra: finding.otherExampleCount,
                  })
                : finding.examples.join(", ")}
            </span>
          ) : null}
        </span>
        <span className="shrink-0 text-xs tabular-nums">
          <span className="text-foreground font-medium">
            {t("preflight.findings.occurrence", {
              count: finding.assetCount,
              total: scoredCount,
            })}
          </span>
          <span className="text-muted-foreground">{" · "}</span>
          <CriterionLabel
            name={finding.criterionName}
            definition={finding.criterionDefinition}
            className="text-muted-foreground"
          />
        </span>
      </div>
      {/* Split fail vs partial like the blockers bar, so a row's composition stays legible. */}
      <div className="bg-muted flex h-2 w-full gap-px overflow-hidden rounded-full">
        <span
          className={cn(
            "h-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
            TONE_BG.error,
          )}
          style={{ width: mounted ? `${failWidth}%` : "0%" }}
        />
        {partialWidth > 0 ? (
          <span
            className={cn(
              "h-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
              TONE_BG.warning,
            )}
            style={{ width: mounted ? `${partialWidth}%` : "0%" }}
          />
        ) : null}
      </div>
    </li>
  );
}

export interface ReportFindingsProps {
  report: PreflightReportDetail;
  /** Render just the panel body (no collapsible section) — used inside the insights tabs. */
  headless?: boolean;
  className?: string;
}

/**
 * Which sub-check the analyzer failed, ranked by how many creatives failed it. A criterion is a
 * bundle of sub-checks, and the sub-check is the production note — "Max words per frame" is
 * something a producer can act on, "Focus on 1 Main Message scored 0.4" is not. Today a
 * sub-check is reachable only one creative at a time, inside an expanded breakdown row.
 */
export function ReportFindings({
  report,
  headless = false,
  className,
}: ReportFindingsProps) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  const findings = useMemo(() => computeReportFindings(report), [report]);
  // Same set the aggregation counted over, so "12 of 20" can never disagree with itself.
  const scoredCount = useMemo(() => scoredAssetCount(report), [report]);
  const maxAssetCount = Math.max(
    0,
    ...findings.map((finding) => finding.assetCount),
  );

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  if (findings.length === 0) return null;

  const body = (
    <TooltipProvider delayDuration={250} skipDelayDuration={100}>
      <div className="flex flex-col gap-3">
        <p className="text-muted-foreground text-sm">
          {t("preflight.findings.description", { count: scoredCount })}
        </p>
        <ol
          className={cn(
            "-mx-2 flex flex-col overflow-y-auto",
            LIST_MAX_HEIGHT_CLASS,
          )}
        >
          {findings.map((finding, index) => (
            <FindingRow
              key={finding.key}
              finding={finding}
              rank={index + 1}
              scoredCount={scoredCount}
              maxAssetCount={maxAssetCount}
              mounted={mounted}
            />
          ))}
        </ol>
      </div>
    </TooltipProvider>
  );

  if (headless) return body;

  return (
    <CollapsibleSection
      title={t("preflight.findings.title")}
      className={className}
    >
      <Card className="gap-0 border-border/60 p-5">{body}</Card>
    </CollapsibleSection>
  );
}
